import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { IconButton, Tooltip as MuiTooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const DiasPromedioEntregaBar = ({ selectedIndustries = [], onIndustryClick = () => {} }) => {
  const [infoOpen, setInfoOpen] = useState(false);

  // Etiquetas originales
  const fixedLabels = [
    'Servicios logísticos',
    'Construcción',
    'Salud/Farmacéutica',
    'Alimentos',
    'Automotriz',
    'Retail',
    'Manufactura'
  ];

  // Datos para 2023 (antes se llamaba 2023 pero la etiqueta lo llama 2022 en el dataset)
  const diasEntregaData2023 = {
    'Servicios logísticos': 91,
    'Construcción': 85,
    'Salud/Farmacéutica': 90,
    'Alimentos': 91,
    'Automotriz': 93,
    'Retail': 91,
    'Manufactura': 94,
  };

  // Datos para 2024 (antes se llamaba 2024 pero la etiqueta lo llama 2023 en el dataset)
  const diasEntregaData2024 = {
    'Servicios logísticos': 93,
    'Construcción': 91,
    'Salud/Farmacéutica': 100,
    'Alimentos': 92,
    'Automotriz': 89,
    'Retail': 80,
    'Manufactura': 85,
  };

  // Cambio (1): Se usará solo un color para cada dataset (azul y gris)
  const colorAzul = '#1F3D58';
  const colorGris = '#717171';

  const labels = selectedIndustries.length > 0 ? selectedIndustries : fixedLabels;
  const dataValues2023 = labels.map((label) => diasEntregaData2023[label]);
  const dataValues2024 = labels.map((label) => diasEntregaData2024[label]);

  // Cambio (2): Eliminamos los colorMaps por industria, y usamos un solo color por dataset
  const chartData = {
    labels: labels,
    datasets: [
      {
        label: '% Entregas a tiempo (2022)', // Si lo deseas, puedes renombrarlo a 'Promedio de días (2023)'
        data: dataValues2023,
        backgroundColor: colorAzul, // azul
        borderColor: '#fff',
        borderWidth: 1,
      },
      {
        label: '% Entregas a tiempo (2023)', // Si lo deseas, puedes renombrarlo a 'Promedio de días (2024)'
        data: dataValues2024,
        backgroundColor: colorGris, // gris
        borderColor: '#fff',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20,
        bottom: 30,
        left: 10,
        right: 10,
      },
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw} días`,
        },
      },
      datalabels: {
        color: '#fff',
        font: {
          weight: 'bold',
          size: 12,
        },
        formatter: (value) => `${value}`,
        anchor: 'end',
        align: 'end',
        offset: -25,
        clip: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Industria',
          font: {
            size: 12,
            weight: '',
          },
        },
        ticks: {
          autoSkip: false,
          font: {
            size: 12,
          },
        },
      },
      y: {
        title: {
          display: true,
          text: '% de entregas a tiempo',
          font: {
            size: 12,
            weight: '',
          },
        },
        beginAtZero: true,
        ticks: {
          stepSize: 2,
          font: {
            size: 12,
          },
        },
      },
    },
  };

  const infoContent = {
    Analisis: 'En 2023 refleja avances significativos en sectores como Salud/Farmacéuticas, Construcción, Alimentos y Servicios logísticos, destacando el logro del 100% de cumplimiento en el sector Salud.',
    formula: 'Entregas a tiempo de proveedor= (𝑇𝑜𝑡𝑎𝑙 𝑑𝑒 ó𝑟𝑑𝑒𝑛𝑒𝑠 𝑑𝑒 𝑐𝑜𝑚𝑝𝑟𝑎 𝑟𝑒𝑐𝑖𝑏𝑖𝑑𝑎𝑠 𝑎 𝑡𝑖𝑒𝑚𝑝𝑜 /𝑇𝑜𝑡𝑎𝑙 𝑑𝑒 ó𝑟𝑑𝑒𝑛𝑒𝑠 𝑑𝑒 𝑐𝑜𝑚𝑝𝑟𝑎 𝑠𝑜𝑙𝑖𝑐𝑖𝑡𝑎𝑑𝑎𝑠 𝑎 p𝑟𝑜𝑣𝑒𝑒𝑑𝑜𝑟) 𝑥 100',
    description: 'Esta grafica muestra el promedio de las entregas a tiempo por parte del proveedor este indicador refleja la puntualidad en el abastecimiento por parte de proveedores en el 2022 y 2023.',
  };

  return (
    <div style={{ maxWidth: '800px', height: '515px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>
      <Bar data={chartData} options={options} />
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Análisis:</strong> {infoContent.Analisis}</p>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DiasPromedioEntregaBar;
